import { Component } from '@angular/core';

import { translate } from '@jsverse/transloco';

import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { ScatterPlotUtils } from 'app/utils/scatter-plot-utils';
import { Utils } from 'app/utils/utils';
import { Comparison } from '../../../../providers/bridge-api/dashboard.service';
import { WidgetChartComponent } from '../widget-chart/widget-chart.component';
import { ChartAiInsightPointSelectionService } from '../widget-expansion-panel/widget-ai-insights/chart-ai-insight-point-selection.service';
import { ChartWidgetAiInsightsValidatorService } from '../widget-expansion-panel/widget-ai-insights/services/validation/chart-widget-ai-insights-validator.service';
import { WidgetAiInsightsValidatorService } from '../widget-expansion-panel/widget-ai-insights/services/validation/widget-ai-insights-validator.service';
import { IMPACT_REPORT_DATE_FORMAT } from '../widget-impact/widget-impact.component';
import { ImpactReport } from '../../services/chart-report';

@EnableClassTelemetry()
@Component({
  selector: 'app-widget-chart-scatterplot',
  templateUrl: '../widget-chart/widget-chart.component.html',
  styleUrls: ['../widget-chart/widget-chart.component.scss'],
  providers: [
    ChartAiInsightPointSelectionService,
    { provide: WidgetAiInsightsValidatorService, useClass: ChartWidgetAiInsightsValidatorService },
  ],
  standalone: false,
})
export class WidgetChartScatterplotComponent extends WidgetChartComponent {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  refresh(resetUserDateInterval?: boolean, initialLoad?: boolean): Promise<any> {
    this.canRemoveFromIPWG =
      !this.widgetGroup?.isImpact ||
      this.widget.payload.type !== 'scatterPlot' ||
      (!this.widget.payload.xAxis.includes('impact') &&
        !this.widget.payload.yAxis.includes('impact'));
    this.removeFromIPWGTooltip = !this.canRemoveFromIPWG
      ? translate('widgets.can-not-remove-widget-group-impact')
      : undefined;

    return super.refresh(resetUserDateInterval, initialLoad);
  }

  public canApplyComparison(comparison?: Comparison): boolean {
    if (!comparison) {
      return true;
    }

    return comparison.filters.length < 3;
  }

  protected async calculateCurrentPeriod(reportOptions): Promise<any> {
    const response: any = {};

    if (!this.widgetGroup?.isImpact) {
      return response;
    }

    if (
      this.widget.payload.xAxis.startsWith('impact') ||
      this.widget.payload.yAxis.startsWith('impact')
    ) {
      const impactReportOptions = JSON.parse(JSON.stringify(this.widget.payload));
      impactReportOptions.widgetDataSource = this.widgetDataSource;
      impactReportOptions.type = 'impact';
      delete impactReportOptions.sentimentScale;

      const params = {
        scoreMetric: this.widgetGroup.parameters.scoreMetric,
        previousStart: this.widgetGroup.parameters.previousStart,
        previousEnd: this.widgetGroup.parameters.previousEnd,
        currentStart: this.widgetGroup.parameters.currentStart,
        currentEnd: this.widgetGroup.parameters.currentEnd,
        dateFormat: IMPACT_REPORT_DATE_FORMAT,
        sortBy: 'previous',
        sortOrder:
          this.widget.payload.xAxis.includes('positive') ||
          this.widget.payload.yAxis.includes('positive')
            ? 'desc'
            : 'asc',
      };

      const impactReport = this.chartReportService.configureReport(
        impactReportOptions
      ) as ImpactReport;

      const impactWidgetQueryOptions = this.filterService.getWidgetQueryOptions(
        this.widget,
        this.widgetDataSource,
        this.widgetGroup
      );

      const impactData = await impactReport.executeReportForImpact(
        params,
        this.widgetDataSource,
        this.filterService.getQueryForDocuments(impactWidgetQueryOptions, false, true)
      );
      response.impactData = impactData;

      if (!reportOptions.selectedItems.length) {
        reportOptions.selectedItems = impactData.data.map((imp) => imp.label);
      }
    }

    const currentWidgetQueryOptions = this.cpppService.assembleWidgetQueryOptions(
      this.widget,
      this.widgetDataSource,
      this.widgetGroup,
      {
        first: this.widgetGroup.parameters.currentStart,
        last: this.widgetGroup.parameters.currentEnd,
      }
    );

    const currentPeriodData = await this.executeReport(
      'json',
      this.widget.payload.drilledDownOn,
      this.filterService.getQueryForDocuments(currentWidgetQueryOptions, false, true)
    );
    response.currentPeriodData = currentPeriodData;
    this.report.options.currentPeriodDocumentCount = this.report.lastDocumentCount;
    this.filteredDocumentCount += this.report.lastDocumentCount;

    if (!reportOptions.selectedItems.length) {
      reportOptions.selectedItems = Object.keys(currentPeriodData.items);
    }

    return response;
  }

  public async exportAsCsv(): Promise<{ data: any }> {
    if (!this.widgetGroup?.isImpact) {
      const { data } = await super.exportAsCsv();

      const rows: string[] = data.split('\n');

      const columns = rows[0].split(',');

      columns[1] = Utils.escapeCsvString(ScatterPlotUtils.getAxisName(this.widget.payload.xAxis));
      columns[2] = Utils.escapeCsvString(ScatterPlotUtils.getAxisName(this.widget.payload.yAxis));

      rows[0] = columns.join();

      return {
        data: rows.join('\n'),
      };
    }

    let data = translate('widgets.csv.scatterplot-header', {
      xAxis: ScatterPlotUtils.getAxisName(this.widget.payload.xAxis),
      yAxis: ScatterPlotUtils.getAxisName(this.widget.payload.yAxis),
    });

    for (const row of this.dataSource) {
      data += `"${Utils.escapeCsvString(row.name)}",${row.xAxis},${row.xAxisPrev},${row.yAxis},${
        row.yAxisPrev
      }\n`;
    }

    return { data };
  }
}
